// @ts-nocheck

import { useContext, FC, useEffect, Children, ReactNode, isValidElement } from "react";
import cn from "classnames";
import Skeleton from "react-loading-skeleton";
import { Controller } from "react-hook-form";

import { SelectFieldProps } from "./Select.props";
import Label from "../Label";
import FieldMessage from "../FieldMessage";
import ReactSelectComponent from "./ReactSelect";
import LabelTextItem from "../../LabelTextItem";

import globalFormStyles from "../global.formElements.module.scss";
import styles from "./Select.module.scss";

import { FormContext } from "../../Form";
import Tag from "../../Tags";

/**
 * @deprecated Use `import { Dropdown } from capitalrx-components-2.0/lib/forms` instead.
 */
const Select: FC<SelectFieldProps> = (props) => {
  const {
    id,
    labelText,
    disabled = false,
    containerExtraClass,
    helperText,
    name,
    validate,
    children,
    readOnly,
    displayReadOnlyFieldInline,
    useAsterisk,
    isLoading,
    testId,
  } = props;

  const { control, formState, getValues, readOnly: readOnlyForm, unregister } = useContext(FormContext);
  const { errors, isFormLoading } = formState;
  const hasErrors = disabled ? undefined : errors;
  const hasValidation = disabled ? undefined : validate;

  useEffect(() => {
    if (disabled) {
      unregister(name);
    }
  }, [disabled, name]);

  const isInvalid = errors && !disabled ? Object.keys(errors)?.length && errors[name] : false;
  const inputContainerClasses = cn(styles["form-field-container"], { disabled: disabled }, containerExtraClass);
  const selectClasses = cn(globalFormStyles["field-wrapper"], { [styles["invalid-select-field"]]: isInvalid });

  /** loops through the children options and create the json react-select needs to add options */
  const selectOptions = Children.map(children, (child: ReactNode) => {
    if (isValidElement(child) && child.props.children && child.props.value !== undefined) {
      return { value: child.props.value, label: child.props.children };
    } else {
      return null;
    }
  });

  let component = null;
  const testVal = testId ? testId : `test__${id}`;
  if (isLoading || isFormLoading) {
    component = <Skeleton height={40} />;
  } else {
    component = (
      <div className={selectClasses} data-testid={testVal}>
        <Controller
          control={control}
          name={name}
          rules={hasValidation}
          render={({ field }): JSX.Element => {
            let component = null;

            if (readOnly || readOnlyForm) {
              let readOnlyValue: ReactNode = null;

              if (Array.isArray(field.value)) {
                const entries = [];
                for (const value of field.value) {
                  const option = selectOptions?.find((option) => option.value === value);
                  if (!option) {
                    entries.push(
                      <Tag key={value} color="darkgrey" size="small">
                        {value}
                      </Tag>,
                    );
                  } else {
                    entries.push(
                      <Tag key={value} color="darkgrey" size="small">
                        {option.label}
                      </Tag>,
                    );
                  }
                }
                readOnlyValue = entries;
              } else {
                const value = getValues && getValues(name);
                const option = selectOptions?.find((option) => option.value === value);
                if (!option) readOnlyValue = value;
                else readOnlyValue = option.label;
              }

              if (readOnlyValue === null) {
                readOnlyValue = "--";
              } else if (readOnlyValue === undefined) {
                readOnlyValue = "Unspecified";
              }

              component = (
                <LabelTextItem
                  id={id}
                  label={labelText}
                  displayInline={displayReadOnlyFieldInline}
                  body={<div>{readOnlyValue}</div>}
                  isLoading={isLoading || isFormLoading}
                />
              );
            } else {
              component = (
                <div className={styles["select-component-wrapper"]}>
                  <ReactSelectComponent {...props} field={field} isInvalid={!!isInvalid}>
                    {children}
                  </ReactSelectComponent>
                </div>
              );
            }

            return (
              <>
                {labelText && !(readOnly || readOnlyForm) && (
                  <Label
                    id={id}
                    label={labelText}
                    disabled={disabled}
                    isLoading={isLoading || isFormLoading}
                    validate={hasValidation}
                    useAsterisk={useAsterisk}
                  />
                )}
                {component}
                {!(readOnly || readOnlyForm) && (
                  <FieldMessage id={id} helperText={helperText} errors={hasErrors} name={name} isLoading={isLoading || isFormLoading} />
                )}
              </>
            );
          }}
        />
      </div>
    );
  }

  return <div className={inputContainerClasses}>{component}</div>;
};

/**
 * @deprecated
 */
export default Select;
